import { FilterBar } from '@/components/filters';
import { AddFilterButtonDropdown } from '@/components/filters/AddFilterButton';
import { SortButtonDropdown, SortItem } from '@/components/filters/SortButton';
import { useGetProjectTransactionFiltersQuery } from '@/services/payouts';
import { Box } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo } from 'react';

import { AppliedDataFilter, ProjectTransactionSort } from 'types';

interface PayoutTransactionsFilterProps {
  appliedFilters?: AppliedDataFilter[];
  setAppliedFilters: (filters: AppliedDataFilter[]) => void;
  searchParams?: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  searchTerm?: string;
  advancedFilters?: string;
  appliedSort?: SortItem;
  handleSortSelection: (sort: SortItem) => void;
  hideSortOptions?: ProjectTransactionSort[];
  hideFilterOptions?: PayoutTransactionFilterName[];
  searchPlaceholder?: string;
}

export type PayoutTransactionFilterName =
  | 'projectName'
  | 'payeeName'
  | 'amount'
  | 'eventDate'
  | 'payoutEvent'
  | 'projectExternalId'
  | 'status'
  | 'clawbackRuleName'
  | 'isManual'
  | 'payoutHold'
  | 'programType'
  | 'batchDate'
  | 'pauseExpirationDate'
  | 'pausedDueToPaymentPlan'
  | 'payeeType'
  | 'transactionLevel'
  | 'generalLedgerCategory'
  | 'payeePayoutEvent';

interface SortOption {
  id: ProjectTransactionSort;
  label: string;
}
export const transactionSortOptions: Array<SortOption> = [
  { id: 'payeeNameAsc', label: 'Payee A-Z' },
  { id: 'payeeNameDesc', label: 'Payee Z-A' },
  { id: 'amountAsc', label: 'Amount Low-High' },
  { id: 'amountDesc', label: 'Amount High-Low' },
  { id: 'eventDateAsc', label: 'Event Date Old-New' },
  { id: 'eventDateDesc', label: 'Event Date New-Old' },
  { id: 'projectNameAsc', label: 'Account A-Z' },
  { id: 'projectNameDesc', label: 'Account Z-A' },
  { id: 'eventAsc', label: 'Event A-Z' },
  { id: 'eventDesc', label: 'Event Z-A' },
  { id: 'statusAsc', label: 'Status A-Z' },
  { id: 'statusDesc', label: 'Status Z-A' },
  { id: 'projectExternalIdAsc', label: 'Account Id A-Z' },
  { id: 'projectExternalIdDesc', label: 'Account Id Z-A' },
  { id: 'pausedExpirationDateAsc', label: 'Paused Expiration Date Old-New' },
  { id: 'pausedExpirationDateDesc', label: 'Paused Expiration Date New-Old' },
  { id: 'batchDateAsc', label: 'Batch Date Old-New' },
  { id: 'batchDateDesc', label: 'Batch Date New-Old' },
];

export const PayoutTransactionsFilter = ({
  setAppliedFilters,
  appliedFilters = [],
  searchParams = new URLSearchParams(),
  setSearchParams,
  searchTerm,
  advancedFilters = '',
  appliedSort,
  handleSortSelection,
  hideSortOptions,
  hideFilterOptions,
  searchPlaceholder = 'Search by Payee, Account, Amount',
}: PayoutTransactionsFilterProps) => {
  const { data = [], isLoading: isFiltersLoading } = useGetProjectTransactionFiltersQuery({});
  const filters = useMemo(() => {
    return data.filter((filter: any) => !hideFilterOptions?.includes(filter.id));
  }, [data, hideFilterOptions]);

  const sortOptions = useMemo(
    () => transactionSortOptions.filter((option) => !hideSortOptions?.includes(option.id)),
    [hideSortOptions],
  );

  const onSearchChange = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm && newSearchTerm !== '') {
        searchParams.set('search', newSearchTerm);
      } else {
        searchParams.delete('search');
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        searchParams.delete('page');
        setSearchParams(searchParams);
      }
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  useEffect(() => {
    const filters = advancedFilters || JSON.stringify([]);
    setAppliedFilters(JSON.parse(filters));
  }, [advancedFilters, setAppliedFilters]);

  return (
    <Box background="grey-100" radius="sm" direction="row" display="block">
      <FilterBar
        filtersState={{
          searchTerm,
        }}
        handleSearchTerm={onSearchChange}
        placeholder={searchPlaceholder}
      >
        <Box direction="row" width="100" gap="md" alignItems="center">
          {!isFiltersLoading && (
            <AddFilterButtonDropdown
              dataFilters={filters}
              appliedFilters={appliedFilters}
              onRemoveFilter={handleRemoveAdvancedFilter}
              onApplyFilters={handleApplyAdvancedFilters}
              onClearFilters={handleClearAdvancedFilters}
            />
          )}
          <SortButtonDropdown
            appliedSort={appliedSort || sortOptions[0]}
            sortOptions={sortOptions}
            onSortChange={handleSortSelection}
            className="sortButton"
          />
        </Box>
      </FilterBar>
    </Box>
  );
};
